import { HEIGHT, MAX_X, MAX_Y, WIDTH, FrenzyColor } from './FrenzyConstants';
import {Direction, DrawMode, Level, LeptonDirection, LeptonState, Rotation, FrenzyRotation, FrenzyDirection, FrenzyPolygon, FrenzyFilledPolygon, FrenzyLine} from './Model';
import { findIntersectingLines, wouldBeInsideExistingFilledArea, pointsToLine, oppositeRotation } from './Utils';
import { describeLines } from './utils/Line';
import { linesToPolygon } from './utils/Polygon';

const MAX_RECURSION_DEPTH = 512;

export const getInitialLines = ():FrenzyLine[] => {
  return [
    { direction: Direction.RIGHT, start: [0, 0], end: [MAX_X, 0]},
    { direction: Direction.DOWN, start: [0, 0], end: [0, MAX_Y]},
    { direction: Direction.RIGHT, start: [0, MAX_Y], end: [MAX_X, MAX_Y]},
    { direction: Direction.DOWN, start: [MAX_X, 0], end: [MAX_X, MAX_Y]}
  ];
};

const TOTAL_PIXELS_IN_PLAYFIELD = HEIGHT * WIDTH;
export const getInitialLeptonStates = (level:Level):LeptonState[] => {
  // We want the first lepton at 100, 100

  if (level.leptonCount === 1) {
    return [{
      leptonId: 1,
      x: 100,
			y: 100,
			direction: LeptonDirection.SE,
    }];
  }

  // After that, let's just take the total number of pixels in the 
  // playfield, divide by number of leptons, see how that goes
  const pixelsPerLepton = Math.floor(WIDTH / (level.leptonCount + 1));

	return Array.from({length: level.leptonCount}, (l, i) => {
    const leptonId = (level.number * 1000) + i;
		return {
      leptonId,
			x: pixelsPerLepton * (i + 1),
			y: 100,
			direction: LeptonDirection.SE
		};
	});
};

export const canPlayerMove = (playerX: number, playerY: number, direction: FrenzyDirection, lines: FrenzyLine[], filledPolygons: FrenzyPolygon[], width?: number, height?: number) => {
  // Check whether moving in this direction would cause the player to
  // end up 'inside' a completed filled polygon.
  if (wouldBeInsideExistingFilledArea(playerX, playerY, direction, filledPolygons)) {
    return false;
  }

  switch (direction) {
    case Direction.UP:
      return playerY > 0;
    case Direction.DOWN:
      return playerY < (height || MAX_Y);
    case Direction.LEFT:
      return playerX > 0;
    case Direction.RIGHT:
			// console.log(`Comparing ${playerX} with ${width || WIDTH}`)
      return playerX < (width || MAX_X);
    default:
      return false;
  }
};

export const canPlayerMoveOnExistingLine = (playerX: number, playerY: number, direction: FrenzyDirection, lines: FrenzyLine[]) => {
  const intersects = findIntersectingLines(playerX, playerY, lines, direction.axis);

  if (intersects.length === 0) {
    return false;
  }

  const canBeMovedOn = intersects.find(l => {
    return direction.canMoveOnLine(playerX, playerY, l);
  });

  return typeof canBeMovedOn !== 'undefined';
};

export const isOnLine = (x: number, y: number, lines: FrenzyLine[]) => {
  const hits = findIntersectingLines(x, y, lines);
  // console.log(`Hit for ${playerX}, ${playerY} is ${hit}`);
  // return (typeof hit !== 'undefined') && ;
  return hits.length > 0;
};

const spacer = (depth:number) => {
  let s = '';
  for (let i = 0; i < depth; i = i + 1) {
    s = s + ' ';
  }
  return s;
};

const canBeTraversed = (candidateLines: FrenzyLine[], desiredDirection: FrenzyDirection, initialX: number, initialY: number) => {
	const { x, y } = desiredDirection.traverse(initialX, initialY); 
	const ok = isOnLine(x, y, candidateLines);	
	//console.log(`At ${initialX},${initialY}, and want to go ${desiredDirection.label}. Checking ${x},${y}: Traversable: ${ok}`);
	return ok;
};

const recursivelyFindFirstIntersectingLine = (candidateLines: FrenzyLine[], startX: number, startY: number, directionOfMostRecentlyCompletedLine: FrenzyDirection, rotation: FrenzyRotation, finalX: number, finalY: number, recursionDepth: number): FrenzyLine[] => {
  let iterationCount = 0;

  if (recursionDepth > MAX_RECURSION_DEPTH) {
    console.warn(`BAILING BEFORE EXHAUSTING STACK`);
    console.warn(`Sought intersects in ${rotation.label} at ${startX}, ${startY}`);
    console.warn(`Failed to find any intersects in ${describeLines(candidateLines)}`);
    return [ pointsToLine([startX, startY], [startX, startX])
    ];
  }


  let searchDirection = rotation.appliedTo(directionOfMostRecentlyCompletedLine);

  // first new test. Is there actually a line here, if we start moving in this direction?
  let { x, y } = searchDirection.traverse(startX, startY);

  while (!isOnLine(x, y, candidateLines)) {

    // Next best option is to try WITHOUT the rotation applied.
    searchDirection = oppositeRotation(rotation).appliedTo(searchDirection);

    const tuple = searchDirection.traverse(startX, startY);
    x = tuple.x;
    y = tuple.y;
  }

  do {
    // console.log(`${spacer(recursionDepth)}Checking ${x}, ${y}`);
    if (x === finalX && y === finalY) {
      // We've done it.
      const finalLine: FrenzyLine = {
        direction: searchDirection, 
        start: [startX, startY], 
        end: [finalX, finalY]
      };

      return [finalLine];
    }

    // Only look for lines that are of the opposite orientation to our current traversal direction
    const intersects = findIntersectingLines(x, y, candidateLines, searchDirection.oppositeAxis);
    if (intersects.length > 0) {
      // const [startX, startY] = o.expressPrimarySecondaryAsXY(primaryAxisStartPoint, secondaryAxisStartPoint);
      // console.log(`${spacer(recursionDepth)} intersect at: ${x},${y}`, describeLine(intersect));

      // We have the end point of our traversed line, which is also the start of the next.
      // Now "turn" `turnDirection` down it, and repeat.
      const shortenedIntersect: FrenzyLine = {
        direction: searchDirection, 
        start: [startX, startY], 
        end: [x, y]
      };

	
      const acc = recursivelyFindFirstIntersectingLine(candidateLines, x, y, searchDirection, rotation, finalX, finalY, recursionDepth + 1);
      const completeResult = [shortenedIntersect].concat(acc);
      return completeResult;
    }
    iterationCount = iterationCount + 1;
    const { x: newX, y: newY } = searchDirection.traverse(x, y);
    x = newX;
    y = newY;
  } while (iterationCount < WIDTH);
  //   const intersectingLineSegment = formIntersectingSegment(firstIntersect, intersectX, intersectY, existingLines, turnDirection);
  return [];
};

/** Return an array of lines that is obtained by following lines,
 * always turning in the given turnDirection when arriving at a junction
 */
export const findEnclosedArea = (existingLines: FrenzyLine[], newlyDrawnLines: FrenzyLine[], rotation: FrenzyRotation):FrenzyLine[] => {
  // Find where the newly drawn lines began:
  const first = newlyDrawnLines[0];
  const firstX = first.start[0];
  const firstY = first.start[1];

  // Find how we finished the last drawn line:
  const lastDrawnLine = newlyDrawnLines[newlyDrawnLines.length - 1];

  // We should begin at the endpoint of the last drawn line.
  // By definition there is an intersection here, we just need to turn the right way and head along it
  const finalX = lastDrawnLine.end[0];
  const finalY = lastDrawnLine.end[1];

  // console.log(`finding enclosed area recursively from ${finalX}, ${finalY} (newly drawn: ${describeLines(newlyDrawnLines)}) in rotation ${rotation.label} ... existingLines: ${describeLines(existingLines)}`);


  const intersects = recursivelyFindFirstIntersectingLine(existingLines, finalX, finalY, lastDrawnLine.direction, rotation, firstX, firstY, 0);
  const enc =  newlyDrawnLines.concat(intersects);
  return enc;
};

export const findSmaller = (fillLines: FrenzyLine[], allLines: FrenzyLine[]):FrenzyPolygon => {
  const ccwLines = findEnclosedArea(allLines, fillLines, Rotation.CCW);
  const cwLines = findEnclosedArea(allLines, fillLines, Rotation.CW);

  const ccwPoly = linesToPolygon(ccwLines);
  const cwPoly = linesToPolygon(cwLines);

  return (ccwPoly.area < cwPoly.area) ? ccwPoly : cwPoly;
};

export const findSmallerFillRectangles = (fillLines: FrenzyLine[], allLines: FrenzyLine[]):FrenzyPolygon => {
  const smallerPolygon = findSmaller(fillLines, allLines);
  // console.log('The smaller polygon is ', describePolygon(smallerPolygon));
  // const fillRectangles = findFillRectangles(smallerPolygon);
  // return fillRectangles;
  return smallerPolygon;
};

export const fillPolygon = (src: FrenzyPolygon, drawMode: DrawMode):FrenzyFilledPolygon => {
	return {
		...src,
		drawMode,
		color: drawMode === DrawMode.SLOW ? FrenzyColor.RED : FrenzyColor.MAGENTA
	}
}
